import React, { useEffect, useState, useCallback } from 'react';

// Utility function to get auth token
const getAuthToken = () => {
  return localStorage.getItem('authToken');
};

interface Prescription {
  id?: number;
  prescription_id?: number;
  patient_name?: string;
  patient_id?: number;
  doctor_name?: string;
  created_at?: string;
  updated_at?: string;
  status?: string;
  total_items?: number;
  doctor_id?: number;
  clinic_id?: number;
  appointment_id?: number | string;
  [key: string]: any;
}

interface DoctorMapping {
  doctor_id: number;
  clinic_id: number;
  name: string;
}

interface PharmaDetails {
  pharma_id?: string;
  count?: number;
  mappings?: DoctorMapping[];
  clinic_id?: number;
  doctors?: any[];
  [key: string]: any;
}

interface PatientsPrescriptionProps {
  onPrescriptionSelect?: (prescription: Prescription) => void;
  hideHeader?: boolean;
}

const PatientsPrescription: React.FC<PatientsPrescriptionProps> = ({ onPrescriptionSelect, hideHeader = false }) => {
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 768);
  const [prescriptions, setPrescriptions] = useState<Prescription[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedPrescriptionId, setSelectedPrescriptionId] = useState<number | null>(null);
  const [doctors, setDoctors] = useState<DoctorMapping[]>([]);
  const [selectedDoctorId, setSelectedDoctorId] = useState<number | null>(null);
  const [clinicId, setClinicId] = useState<number | null>(null);
  const [isDoctorDropdownOpen, setIsDoctorDropdownOpen] = useState<boolean>(false);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (isDoctorDropdownOpen && !target.closest('.doctor-dropdown-container')) {
        setIsDoctorDropdownOpen(false);
      }
    };

    if (isDoctorDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isDoctorDropdownOpen]);

  // Load pharmaDetails from localStorage on mount
  useEffect(() => {
    try {
      const pharmaDetailsStr = localStorage.getItem('pharmaDetails');
      if (pharmaDetailsStr) {
        const pharmaDetails: PharmaDetails = JSON.parse(pharmaDetailsStr);
        console.log('Loaded pharmaDetails:', pharmaDetails);
        
        // Extract mappings array (contains doctor_id, clinic_id, name)
        if (pharmaDetails.mappings && Array.isArray(pharmaDetails.mappings) && pharmaDetails.mappings.length > 0) {
          const mappingsList = pharmaDetails.mappings;
          setDoctors(mappingsList);
          
          // Extract clinic_id from first mapping (assuming all mappings have same clinic_id)
          if (mappingsList[0].clinic_id) {
            setClinicId(mappingsList[0].clinic_id);
          }
          
          // Select the first doctor by default
          if (mappingsList.length > 0) {
            setSelectedDoctorId(mappingsList[0].doctor_id);
            console.log('Selected doctor:', mappingsList[0].doctor_id, 'Clinic:', mappingsList[0].clinic_id);
          }
        } else if (pharmaDetails.clinic_id) {
          // Fallback: if no mappings but clinic_id exists
          setClinicId(pharmaDetails.clinic_id);
        }
      } else {
        console.log('No pharmaDetails found in localStorage');
      }
    } catch (err) {
      console.error('Error parsing pharmaDetails from localStorage:', err);
    }
  }, []);

  const fetchPrescriptions = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const authToken = getAuthToken();
      if (!authToken) {
        throw new Error('Authentication token not found');
      }

      // Determine which API endpoint to use
      let apiUrl = '';
      
      if (selectedDoctorId && clinicId) {
        // Use the new API pattern: /prescriptions/{doctor_id}/{clinic_id}
        apiUrl = `https://pharma-amd-0-1-42.onrender.com/pharma/api/prescriptions/${selectedDoctorId}/${clinicId}`;
        console.log('Fetching prescriptions for doctor:', selectedDoctorId, 'clinic:', clinicId);
        console.log('API URL:', apiUrl);
      } else if (clinicId) {
        // Fallback to clinic_id only if no doctor selected
        apiUrl = `https://pharma-amd-0-1-42.onrender.com/pharma/api/prescriptions/clinic_id/${clinicId}`;
        console.log('Fetching prescriptions for clinic:', clinicId);
        console.log('API URL:', apiUrl);
      } else {
        // Default fallback
        apiUrl = 'https://pharma-amd-0-1-42.onrender.com/pharma/api/prescriptions/clinic_id/1';
        console.log('Using default API URL:', apiUrl);
      }

      console.log('Making API call to:', apiUrl);
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true'
        }
      });

      console.log('API Response status:', response.status);
      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error response:', errorText);
        throw new Error(`Failed to fetch prescriptions: ${response.status}`);
      }

      const data = await response.json();
      console.log('API Response data:', data);
      const list = data.prescriptions || data.data || data || [];
      console.log('Prescriptions list:', list);
      setPrescriptions(Array.isArray(list) ? list : []);
    } catch (err: any) {
      console.error('Error fetching prescriptions:', err);
      setError('Unable to load the prescriptions. Please try again in a moment.');
    } finally {
      setIsLoading(false);
    }
  }, [selectedDoctorId, clinicId]);

  // Load prescriptions when doctor or clinic changes
  useEffect(() => {
    console.log('useEffect triggered - selectedDoctorId:', selectedDoctorId, 'clinicId:', clinicId);
    if (clinicId) {
      console.log('Calling fetchPrescriptions...');
      fetchPrescriptions();
    } else {
      console.log('Skipping fetchPrescriptions - clinicId not set yet');
    }
  }, [clinicId, selectedDoctorId, fetchPrescriptions]);


  const formatDateTime = (value?: string) => {
    if (!value) return 'Date not available';
    try {
      return new Date(value).toLocaleString();
    } catch {
      return value;
    }
  };

  return (
    <div
      style={{
        background: 'white',
        borderRadius: isMobile ? '12px' : '16px',
        padding: isMobile ? '1rem' : '1.5rem',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.06)',
        border: '1px solid #e5e7eb',
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        height: '100%',
      }}
    >
      {/* Header - Only show if not hidden */}
      {!hideHeader && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '0.5rem',
          }}
        >
          <div>
            <h2
              style={{
                fontSize: isMobile ? '1.1rem' : '1.25rem',
                fontWeight: 700,
                color: '#111827',
                margin: 0,
              }}
            >
              Patients Prescriptions
            </h2>
            <p
              style={{
                margin: '0.25rem 0 0 0',
                fontSize: '0.8rem',
                color: '#6b7280',
              }}
            >
              List of recent prescriptions from your pharmacy
            </p>
          </div>
          <button
            type="button"
            onClick={fetchPrescriptions}
            disabled={isLoading}
            style={{
              borderRadius: '9999px',
              border: '1px solid #d1d5db',
              padding: '0.35rem 0.9rem',
              fontSize: '0.8rem',
              fontWeight: 500,
              background: isLoading
                ? 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)'
                : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: '0.35rem',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              boxShadow: isLoading
                ? '0 2px 4px rgba(156, 163, 175, 0.3)'
                : '0 3px 8px rgba(16, 185, 129, 0.35)',
              borderColor: 'transparent',
              transition: 'all 0.2s ease',
            }}
          >
            {isLoading ? (
              <>
                <div
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: '50%',
                    border: '2px solid rgba(255,255,255,0.3)',
                    borderTopColor: 'white',
                    animation: 'spin 1s linear infinite',
                  }}
                />
                <span>Refreshing...</span>
              </>
            ) : (
              <>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 2v6h-6" />
                  <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
                  <path d="M3 22v-6h6" />
                  <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
                </svg>
                <span>Refresh</span>
              </>
            )}
          </button>
        </div>
      )}
      
      {/* Refresh button when header is hidden */}
      {hideHeader && (
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: '0.75rem'
        }}>
          <button
            type="button"
            onClick={fetchPrescriptions}
            disabled={isLoading}
            style={{
              borderRadius: '9999px',
              border: '1px solid #d1d5db',
              padding: '0.35rem 0.9rem',
              fontSize: '0.8rem',
              fontWeight: 500,
              background: isLoading
                ? 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)'
                : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: '0.35rem',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              boxShadow: isLoading
                ? '0 2px 4px rgba(156, 163, 175, 0.3)'
                : '0 3px 8px rgba(16, 185, 129, 0.35)',
              borderColor: 'transparent',
              transition: 'all 0.2s ease',
            }}
          >
            {isLoading ? (
              <>
                <div
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: '50%',
                    border: '2px solid rgba(255,255,255,0.3)',
                    borderTopColor: 'white',
                    animation: 'spin 1s linear infinite',
                  }}
                />
                <span>Refreshing...</span>
              </>
            ) : (
              <>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 2v6h-6" />
                  <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
                  <path d="M3 22v-6h6" />
                  <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
                </svg>
                <span>Refresh</span>
              </>
            )}
          </button>
        </div>
      )}

      {/* Doctor Selector - Show only if multiple doctors */}
      {doctors.length > 1 && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: isMobile ? '0.75rem' : '1rem',
          marginBottom: '1rem',
          padding: isMobile ? '0.75rem 1rem' : '0.875rem 1.25rem',
          background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
          borderRadius: '12px',
          border: '1px solid #bae6fd',
          width: 'fit-content',
          maxWidth: '100%',
          flexWrap: isMobile ? 'wrap' : 'nowrap'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            flexShrink: 0
          }}>
            <svg
              width={isMobile ? "16" : "18"}
              height={isMobile ? "16" : "18"}
              viewBox="0 0 24 24"
              fill="none"
              stroke="#0c4a6e"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
            <label style={{
              fontSize: isMobile ? '0.8rem' : '0.875rem',
              fontWeight: 600,
              color: '#0c4a6e',
              whiteSpace: 'nowrap',
              margin: 0
            }}>
              Select Doctor:
            </label>
          </div>
          <div 
            className="doctor-dropdown-container"
            style={{
              position: 'relative',
              minWidth: isMobile ? '150px' : '200px',
              maxWidth: isMobile ? '100%' : '300px',
            }}
          >
            <button
              type="button"
              onClick={() => setIsDoctorDropdownOpen(!isDoctorDropdownOpen)}
              style={{
                width: '100%',
                padding: isMobile ? '0.5rem 0.75rem' : '0.625rem 1rem',
                borderRadius: '8px',
                border: '2px solid #7dd3fc',
                fontSize: isMobile ? '0.8rem' : '0.875rem',
                fontWeight: 500,
                backgroundColor: 'white',
                color: '#0c4a6e',
                outline: 'none',
                cursor: 'pointer',
                boxSizing: 'border-box',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '0.5rem',
                transition: 'all 0.2s ease',
                boxShadow: isDoctorDropdownOpen ? '0 4px 12px rgba(56, 189, 248, 0.25)' : 'none',
              }}
              onMouseEnter={(e) => {
                if (!isDoctorDropdownOpen) {
                  e.currentTarget.style.borderColor = '#38bdf8';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(56, 189, 248, 0.2)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isDoctorDropdownOpen) {
                  e.currentTarget.style.borderColor = '#7dd3fc';
                  e.currentTarget.style.boxShadow = 'none';
                }
              }}
            >
              <span style={{ 
                overflow: 'hidden', 
                textOverflow: 'ellipsis', 
                whiteSpace: 'nowrap',
                flex: 1,
                textAlign: 'left'
              }}>
                {doctors.find(d => d.doctor_id === selectedDoctorId)?.name || 'Select Doctor'}
              </span>
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#0c4a6e"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{
                  transform: isDoctorDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease',
                  flexShrink: 0
                }}
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>

            {/* Dropdown Menu */}
            {isDoctorDropdownOpen && (
              <div
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 0.5rem)',
                  left: 0,
                  right: 0,
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1), 0 4px 10px rgba(0, 0, 0, 0.05)',
                  zIndex: 1000,
                  overflow: 'hidden',
                  animation: 'dropdownFadeIn 0.2s ease-out',
                  maxHeight: '250px',
                  overflowY: 'auto',
                }}
                className="doctor-dropdown-menu"
              >
                {doctors.map((doctor) => (
                  <button
                    key={doctor.doctor_id}
                    type="button"
                    onClick={() => {
                      setSelectedDoctorId(doctor.doctor_id);
                      setIsDoctorDropdownOpen(false);
                    }}
                    style={{
                      width: '100%',
                      padding: isMobile ? '0.75rem 1rem' : '0.875rem 1.25rem',
                      border: 'none',
                      backgroundColor: selectedDoctorId === doctor.doctor_id 
                        ? 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)' 
                        : 'transparent',
                      background: selectedDoctorId === doctor.doctor_id 
                        ? 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)' 
                        : 'transparent',
                      color: selectedDoctorId === doctor.doctor_id ? '#1e40af' : '#0c4a6e',
                      fontSize: isMobile ? '0.8rem' : '0.875rem',
                      fontWeight: selectedDoctorId === doctor.doctor_id ? 600 : 500,
                      textAlign: 'left',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      transition: 'all 0.15s ease',
                      borderBottom: '1px solid #f1f5f9',
                    }}
                    onMouseEnter={(e) => {
                      if (selectedDoctorId !== doctor.doctor_id) {
                        e.currentTarget.style.backgroundColor = '#f8fafc';
                        e.currentTarget.style.color = '#1e40af';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedDoctorId !== doctor.doctor_id) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = '#0c4a6e';
                      }
                    }}
                  >
                    {selectedDoctorId === doctor.doctor_id && (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ flexShrink: 0 }}
                      >
                        <path d="M20 6L9 17l-5-5" />
                      </svg>
                    )}
                    <span style={{ 
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {doctor.name || `Doctor ${doctor.doctor_id}`}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Content */}
      {isLoading ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: isMobile ? '2.5rem 1rem' : '3rem 1.5rem',
            gap: '0.75rem',
          }}
        >
          <div
            style={{
              width: isMobile ? 40 : 48,
              height: isMobile ? 40 : 48,
              borderRadius: '50%',
              border: '3px solid rgba(59,130,246,0.15)',
              borderTopColor: '#3b82f6',
              animation: 'spin 1s linear infinite',
            }}
          />
          <div
            style={{
              fontSize: '0.9rem',
              fontWeight: 600,
              color: '#374151',
            }}
          >
            Loading prescriptions...
          </div>
          <div
            style={{
              fontSize: '0.8rem',
              color: '#6b7280',
            }}
          >
            Please wait while we fetch the latest data
          </div>
        </div>
      ) : error ? (
        <div
          style={{
            background:
              'linear-gradient(135deg, rgba(239,246,255,0.95) 0%, rgba(219,234,254,0.95) 100%)',
            border: '1px solid rgba(191, 219, 254, 0.8)',
            borderRadius: '10px',
            padding: isMobile ? '1rem' : '1.25rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
          }}
        >
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: '9999px',
              background:
                'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              flexShrink: 0,
            }}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <div style={{ textAlign: 'left' }}>
            <div
              style={{
                fontSize: '0.9rem',
                fontWeight: 600,
                color: '#1d4ed8',
              }}
            >
              Unable to load prescriptions
            </div>
            <div
              style={{
                fontSize: '0.8rem',
                color: '#1e3a8a',
                marginTop: 2,
              }}
            >
              Please wait for a moment and try refreshing.
            </div>
          </div>
        </div>
      ) : prescriptions.length === 0 ? (
        <div
          style={{
            padding: isMobile ? '1.5rem 1rem' : '2rem 1.5rem',
            textAlign: 'center',
            color: '#6b7280',
            borderRadius: '10px',
            background: 'rgba(249,250,251,0.9)',
            border: '1px solid #e5e7eb',
          }}
        >
          <svg
            width="40"
            height="40"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            style={{ marginBottom: '0.75rem', opacity: 0.6 }}
          >
            <rect x="3" y="4" width="18" height="16" rx="2" />
            <path d="M7 8h10" />
            <path d="M7 12h6" />
          </svg>
          <div
            style={{
              fontSize: '0.95rem',
              fontWeight: 600,
              marginBottom: 4,
            }}
          >
            No prescriptions found
          </div>
          <div style={{ fontSize: '0.8rem' }}>
            New prescriptions will appear here once created.
          </div>
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: '0.75rem',
            maxHeight: isMobile ? 360 : 420,
            overflowY: 'auto',
            paddingRight: '0.25rem',
          }}
        >
          {prescriptions.map((p, index) => {
            const id = p.id ?? p.prescription_id ?? index + 1;
            const prescriptionId = p.id ?? p.prescription_id;
            const patientId = p.patient_id;
            
            return (
              <div
                key={id}
                onClick={async () => {
                  setSelectedPrescriptionId(id);
                  
                  // If we have prescription_id, clinic_id, and patient_id, fetch the specific prescription
                  if (prescriptionId && clinicId && patientId && onPrescriptionSelect) {
                    try {
                      const authToken = getAuthToken();
                      if (authToken) {
                        const response = await fetch(
                          `https://pharma-amd-0-1-42.onrender.com/pharma/api/prescriptions/${prescriptionId}/${clinicId}/${patientId}`,
                          {
                            method: 'GET',
                            headers: {
                              'Authorization': `Bearer ${authToken}`,
                              'Content-Type': 'application/json',
                              'ngrok-skip-browser-warning': 'true'
                            }
                          }
                        );
                        
                        if (response.ok) {
                          const prescriptionData = await response.json();
                          onPrescriptionSelect(prescriptionData);
                        } else {
                          // Fallback to original prescription data if API call fails
                          onPrescriptionSelect(p);
                        }
                      } else {
                        // Fallback if no auth token
                        onPrescriptionSelect(p);
                      }
                    } catch (err) {
                      console.error('Error fetching specific prescription:', err);
                      // Fallback to original prescription data on error
                      if (onPrescriptionSelect) {
                        onPrescriptionSelect(p);
                      }
                    }
                  } else if (onPrescriptionSelect) {
                    // Fallback to original prescription data if we don't have all required IDs
                    onPrescriptionSelect(p);
                  }
                }}
                style={{
                  background:
                    selectedPrescriptionId === id
                      ? 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)'
                      : 'linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)',
                  borderRadius: '10px',
                  border:
                    selectedPrescriptionId === id
                      ? '1px solid rgba(59,130,246,0.7)'
                      : '1px solid #e5e7eb',
                  padding: '0.85rem 0.9rem',
                  boxShadow:
                    selectedPrescriptionId === id
                      ? '0 0 0 1px rgba(59,130,246,0.15), 0 4px 10px rgba(15,23,42,0.08)'
                      : '0 2px 6px rgba(15,23,42,0.03)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.35rem',
                  fontSize: '0.8rem',
                  cursor: 'pointer',
                  transition: 'box-shadow 0.15s ease, border-color 0.15s ease, transform 0.1s ease',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)';
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: '0.5rem',
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontWeight: 600,
                        color: '#111827',
                        marginBottom: 2,
                      }}
                    >
                      Prescription #{id}
                    </div>
                    {p.patient_name && (
                      <div
                        style={{
                          color: '#4b5563',
                          fontSize: '0.8rem',
                        }}
                      >
                        Patient: <span style={{ fontWeight: 500 }}>{p.patient_name}</span>
                      </div>
                    )}
                    {p.doctor_name && (
                      <div
                        style={{
                          color: '#6b7280',
                          fontSize: '0.78rem',
                          marginTop: 2,
                        }}
                      >
                        Doctor: {p.doctor_name}
                      </div>
                    )}
                  </div>
                  {p.status && (
                    <div
                      style={{
                        padding: '0.15rem 0.55rem',
                        borderRadius: '9999px',
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        background:
                          p.status === 'completed' || p.status === 'dispensed'
                            ? 'rgba(16,185,129,0.12)'
                            : 'rgba(59,130,246,0.08)',
                        color:
                          p.status === 'completed' || p.status === 'dispensed'
                            ? '#047857'
                            : '#1d4ed8',
                      }}
                    >
                      {p.status}
                    </div>
                  )}
                </div>
                <div
                  style={{
                    marginTop: 4,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    color: '#6b7280',
                  }}
                >
                  <div style={{ fontSize: '0.75rem' }}>
                    {formatDateTime(p.created_at || p.updated_at)}
                  </div>
                  {p.total_items != null && (
                    <div
                      style={{
                        fontSize: '0.75rem',
                        color: '#4b5563',
                      }}
                    >
                      Items: <span style={{ fontWeight: 600 }}>{p.total_items}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @keyframes dropdownFadeIn {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .doctor-dropdown-menu::-webkit-scrollbar {
          width: 6px;
        }

        .doctor-dropdown-menu::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 10px;
        }

        .doctor-dropdown-menu::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 10px;
        }

        .doctor-dropdown-menu::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }

        .doctor-dropdown-menu {
          scrollbar-width: thin;
          scrollbar-color: #cbd5e1 #f1f5f9;
        }
      `}</style>
    </div>
  );
};

export default PatientsPrescription;


